import {
  assetMaintenanceItemsResponse,
  maintenanceItemEventHistoryResponse,
  markMaintenanceItemServicedResponse,
  voidMaintenanceItemEventResponse,
  type AssetMaintenanceItemsResponse,
  type MaintenanceItemEventHistoryResponse,
  type MarkMaintenanceItemServicedBody,
  type MarkMaintenanceItemServicedResponse,
  type VoidMaintenanceItemEventResponse,
} from "@bitacorapro/api-types";
import { request } from "../client";

// GET /assets/:assetId/maintenance-items — the "Mantenciones" panel
// source (ADR-0083). No `create`/admin methods here — the maintenance-
// item catalog and its per-store assignment admin surface are a
// deferred follow-up (mirrors checklist-templates.ts's own precedent:
// that catalog has no create-via-UI either, it loads out-of-band).
export const maintenanceApi = {
  listForAsset: async (assetId: string): Promise<AssetMaintenanceItemsResponse> => {
    const json = await request<unknown>("GET", "core", `/assets/${assetId}/maintenance-items`);
    return assetMaintenanceItemsResponse.parse(json);
  },
  // POST /assets/:assetId/maintenance-items/:itemId/events — "marcar
  // mantención realizada". Sends a client-generated Idempotency-Key per
  // call (ADR-0086 Consequences): unlike the observation-status write
  // this pattern is otherwise modeled on, a mark-serviced write is
  // append-only, not idempotent by nature — a lost response followed by
  // a retry on marginal signal would otherwise double-write a permanent
  // audit row. The header is forward-compatible: core-api doesn't
  // register the @bitacorapro/idempotency plugin on this route yet
  // (tracked separately), so it's currently a no-op server-side, not a
  // guarantee.
  markServiced: async (
    assetId: string,
    itemId: string,
    body: MarkMaintenanceItemServicedBody = {}
  ): Promise<MarkMaintenanceItemServicedResponse> => {
    const json = await request<unknown>(
      "POST",
      "core",
      `/assets/${assetId}/maintenance-items/${itemId}/events`,
      { body, headers: { "Idempotency-Key": crypto.randomUUID() } }
    );
    return markMaintenanceItemServicedResponse.parse(json);
  },
  // GET /assets/:assetId/maintenance-items/:itemId/events — per-item
  // service history, the detail popover's "Historial" source (ADR-0086).
  history: async (
    assetId: string,
    itemId: string
  ): Promise<MaintenanceItemEventHistoryResponse> => {
    const json = await request<unknown>(
      "GET",
      "core",
      `/assets/${assetId}/maintenance-items/${itemId}/events`
    );
    return maintenanceItemEventHistoryResponse.parse(json);
  },
  // POST .../events/:eventId/void — the compensating control for the
  // widened write (ADR-0086). ORG_SCOPED_ROLES only, server-enforced;
  // this client method doesn't gate who may call it.
  voidEvent: async (
    assetId: string,
    itemId: string,
    eventId: string,
    reason?: string
  ): Promise<VoidMaintenanceItemEventResponse> => {
    const json = await request<unknown>(
      "POST",
      "core",
      `/assets/${assetId}/maintenance-items/${itemId}/events/${eventId}/void`,
      { body: reason ? { reason } : {} }
    );
    return voidMaintenanceItemEventResponse.parse(json);
  },
};
